import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, RotateCcw } from "lucide-react";
import { Button, Card, Input, NoticeBox, SegmentedControl, Switch, Tags } from "../../ui";
import { categoryLabel } from "../../copilot/copilotText";
import { fillTemplate } from "./copilotSettingsText";

const SAMPLE_REFERENCE = "COP-000123";

// The message as the customer sees it: LUD Support bubble.
function Preview({ text, senderName, lang, t, customerName }) {
  const filled = fillTemplate(text, { customer_name: customerName, reference: SAMPLE_REFERENCE });
  return (
    <div className="cs-preview" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang}>
      <span className="cs-preview-who">{senderName}</span>
      <div className="cs-preview-bubble">{filled}</div>
      <span className="cs-preview-note">{t.auto.preview}</span>
    </div>
  );
}

function Editor({ t, lang, template, max, canSave, saving, onSave, onCancel, onReset, senderName }) {
  const [value, setValue] = useState(template.text);
  const [error, setError] = useState(null);
  const box = useRef(null);
  useEffect(() => { box.current?.focus(); }, []);

  const insert = (placeholder) => {
    const el = box.current;
    const token = `{{${placeholder}}}`;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    setValue(next);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); });
  };

  const submit = (event) => {
    event.preventDefault();
    if (saving) return;
    if (!value.trim()) return setError(t.auto.empty);
    if (value.length > max) return setError(t.auto.tooLong(max));
    setError(null);
    onSave(value);
  };

  return (
    <form className="cs-editor" onSubmit={submit} noValidate>
      <Input
        textarea
        id={`cs-auto-${lang}`}
        ref={box}
        label={t.auto.editorLabel}
        rows={7}
        dir={lang === "ar" ? "rtl" : "ltr"}
        value={value}
        onChange={(e) => { setValue(e.target.value); setError(null); }}
        error={Boolean(error)}
        aria-describedby={`cs-auto-${lang}-help`}
        disabled={saving}
      />
      <div className="cs-editor-meta" id={`cs-auto-${lang}-help`}>
        <span className="cs-chips">
          <span>{t.auto.placeholders}</span>
          <Button size="xs" variant="primary" appearance="soft" className="cs-chip" onClick={() => insert("customer_name")} disabled={saving}>{"{{customer_name}}"}</Button>
          <Button size="xs" variant="primary" appearance="soft" className="cs-chip" onClick={() => insert("reference")} disabled={saving}>{"{{reference}}"}</Button>
        </span>
        <span className={`cs-count ${value.length > max ? "over" : ""}`}>{t.auto.count(value.length, max)}</span>
      </div>
      <p className="cs-hint">{t.auto.placeholderHelp}</p>
      {error && <p className="cs-error" role="alert">{error}</p>}
      <Preview text={value} senderName={senderName} lang={lang} t={t} customerName={t.auto.sampleName} />
      <div className="cs-actions">
        <Button type="submit" variant="primary" appearance="filled" loading={saving} disabled={!canSave}>{t.save}</Button>
        <Button variant="neutral" appearance="outline" disabled={saving} onClick={onCancel}>{t.cancel}</Button>
        {template.customized && (
          <Button variant="neutral" appearance="ghost" icon={<RotateCcw size={14} />} disabled={saving} onClick={onReset}>{t.auto.reset}</Button>
        )}
      </div>
    </form>
  );
}

// Automatic first replies: on/off, one card per category for the chosen language, edit + preview.
export default function AutoReplySettings({ t, lang, settings, categories, canManage, busy, onToggle, onSaveTemplate, onResetTemplate }) {
  const [language, setLanguage] = useState(lang === "ar" ? "ar" : "en");
  const [editing, setEditing] = useState(null); // category value
  const auto = settings.autoReply;
  const list = useMemo(() => settings.categories || [], [settings.categories]);

  return (
    <div className="cs-stack">
      <Card title={t.auto.title} subtitle={t.auto.description} hoverable={false}>
        <div className="cs-toggle-row">
          <Switch checked={auto.enabled} disabled={!canManage || busy === "enabled"} onChange={(e) => onToggle(e.target.checked)} label={t.auto.enabled} />
          <Tags variant={auto.enabled ? "success" : "neutral"}>{auto.enabled ? t.on : t.off}</Tags>
        </div>
        {!canManage && <NoticeBox variant="info" size="sm">{t.viewOnly}</NoticeBox>}
        <p className="cs-hint">{t.auto.appliesToNew}</p>
      </Card>

      <div className="cs-lang-bar">
        <SegmentedControl
          label={t.auto.language}
          value={language}
          options={[{ value: "ar", label: t.arabic }, { value: "en", label: t.english }]}
          onChange={(code) => { setLanguage(code); setEditing(null); }}
        />
        <span className="cs-hint">{t.auto.languageHint}</span>
        <span className="cs-sender">{t.auto.sender}: <strong>{auto.senderName[language]}</strong></span>
      </div>

      <div className="cs-cards">
        {list.map((category) => {
          const template = auto.templates[category.value][language];
          const isEditing = editing === category.value;
          return (
            <Card
              key={category.value}
              hoverable={false}
              title={categoryLabel(categories, category.value, lang)}
              actions={
                <Tags variant={template.customized ? "primary" : "neutral"}>{template.customized ? t.auto.customized : t.auto.default}</Tags>
              }
            >
              {isEditing ? (
                <Editor
                  t={t}
                  lang={language}
                  template={template}
                  max={auto.maxLength}
                  canSave={canManage}
                  saving={busy === `${category.value}.${language}`}
                  senderName={auto.senderName[language]}
                  onCancel={() => setEditing(null)}
                  onSave={async (text) => { if (await onSaveTemplate(category.value, language, text)) setEditing(null); }}
                  onReset={async () => { if (await onResetTemplate(category.value, language)) setEditing(null); }}
                />
              ) : (
                <>
                  <Preview text={template.text} senderName={auto.senderName[language]} lang={language} t={t} customerName={t.auto.sampleName} />
                  {canManage && (
                    <div className="cs-actions">
                      <Button size="sm" variant="neutral" appearance="outline" icon={<Pencil size={14} />} onClick={() => setEditing(category.value)}>{t.auto.edit}</Button>
                      {template.customized && (
                        <Button size="sm" variant="neutral" appearance="ghost" icon={<RotateCcw size={14} />} loading={busy === `${category.value}.${language}`} onClick={() => onResetTemplate(category.value, language)}>{t.auto.reset}</Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
